
import {
  ref,
  computed,
  defineComponent,
  PropType,
  reactive,
  toRefs,
  watch,
  onMounted,
  WritableComputedRef
} from 'vue'

// import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import ListHeader from '@/components/ListHeader.vue'
import QuantityControl from '@/components/QuantityControl.vue'
import StatusIndicator, { StatusType } from '@/components/StatusIndicator.vue'
import { Entry, EntryConfig, RequestHandler } from '@Protium/vue-ozone-utils'
import { OrderProduct } from '@/models/interfaces/order'
import { Dimensions, EntryCollection, SelectOption } from '@/models/generics'
import { CtOrder, CtPacking, PackingItem } from '@/models/interfaces/content-type'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import { $setTo, debounce, notEmpty } from '@/utils'
import { Status } from '@/models/status'
import { createCanvas, getCanvasDPI, formatStatus, capitalize } from '@/utils/template'
import { ItemViewMode } from '@/constants'
import Clipable from '@/components/Clipable.vue'
import SelectableItem from '@/components/SelectableItem.vue'
import SelectableList from '@/components/SelectableList.vue'
import QuantityContainer from '../components/QuantityContainer.vue'

import usePackageParser from './composables/usePackageParser'
import useProductReplacement from './composables/useProductReplacement'
import useProductOrdering from './composables/useProductOrdering'
import { ProductSelection, ReplacementInstruction, OrderListProductState, ProductDragState, ProductView, ReplacementType } from './interfaces'
import _ from 'lodash'
import { useQuasar } from 'quasar'

// NOTE default packing for items that do not specify packing of their own
// meaning they will usually be included in other packing items in the order (or do not require additional packing)
// optionally, the sizes input are the product sizes (i.e the products own box)
// COMBAK TODO get from api?
const DEFAULT_PACKING = (dimensions?: Dimensions): Array<CtPacking> => ([{
  sys: { id: '' as UUID, type: '' },
  id: Date.now().toString(16) + Math.random().toFixed(),
  name: 'pre-packed',
  weight: 0,
  dimensions: dimensions || {
    x: 0, y: 0, z: 0
  }
}])

const cancelableStatus = [Status.New, Status.Pending, Status.Failed, Status.Paused, Status.Queued, Status.Starting, Status.Active, Status.Changed]
const replaceableStatus = [Status.Stopped, Status.Resolved, Status.Final, Status.Cancelled]
const replacementReasons = [
  'Quality issue',
  'Wrong product',
  'Missing product',
  'Product damaged',
  'Product damaged during delivery',
  'Product perished during delivery',
  'Shipment address issue (not delivered)',
  'Shipment carrier did not deliver on time',
  'Shipment carrier failed to deliver',
  'Shipment carrier unjustified return'
]

export default defineComponent({
  name: 'ProductList',
  components: { ListHeader, StatusIndicator, QuantityControl, Clipable, SelectableItem, SelectableList, QuantityContainer },
  props: {
    id: {
      type: String,
      default: Date.now().toString(16)
    },
    label: {
      type: String,
      default: null
    },
    status: {
      type: Array as PropType<Array<Status>>
    },
    items: {
      type: Array as PropType<Array<OrderProduct>>,
      required: true
    },
    packing: {
      type: Array as PropType<Array<CtPacking>>
    },
    collections: {
      type: Object as PropType<EntryCollection>
    },
    view: {
      type: String as PropType<ProductView>
    },
    config: {
      type: Object as PropType<EntryConfig>
    },
    request: {
      type: Object as PropType<RequestHandler<Entry>>
    },
    showIssues: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    itemView: {
      type: Number as PropType<ItemViewMode>,
      default: ItemViewMode.Default
    },
    packed: {
      type: Array as PropType<Array<{ id: string, components: Record<string, boolean | number> }>>,
      default: () => ([])
    }
  },
  emits: ['select', 'edit', 'update:packing', 'update:items', 'update:packed', 'replaced'],
  setup (props, { emit }) {
    const $store = useStore()
    const $q = useQuasar()
    const $directr = props.request || $store.getters['Services/Directr']
    const statusMap = {
      draft: StatusType.Draft,
      stopped: StatusType.Danger,
      paused: StatusType.Warn,
      cancelled: StatusType.Disabled,
      unsourced: StatusType.Neutral,
      started: StatusType.Highlight,
      sourcing: StatusType.Default,
      resolved: StatusType.Success,
      sourced: StatusType.Success,
      final: StatusType.Success
    }

    const packingEntries = ref<Array<Entry>>([])
    const packingOptions = computed(() => {
      return (props.collections?.packing?.length ? props.collections.packing : packingEntries.value).map(packing => ({ value: packing, label: packing.name }))
    })

    // -----

    const productState = reactive<OrderListProductState>({
      loading: false,
      show: false,
      edit: false,
      diff: false,
      showPackingOptions: undefined,
      replace: {
        products: computed({
          get: () => selectionState.value,
          set: (value) => {
            selectionState.value = value as any
          }
        }),
        reason: null,
        type: { label: 'Default', value: ReplacementType.Default }
      },
      items: [],
      selection: []
    })

    const selectionState = ref<Array<ProductSelection>>([])
    const startingState = ref<Array<PackingItem>>()
    const fulfillableProductTotal = computed(() => parsedPackages.value.reduce((sum: number, item) => sum + (item.products?.filter(f => f.status !== 'cancelled').length || 0), 0))

    // -- State for @Packing sorting / product re-assigning using drag
    const drafts = computed(() => {
      return productState.selection.map(({ sys: { id }, title, sku, ean, quantity, weight }) =>
        ({ id, title, sku, ean, quantity, weight: !weight ? '-' : parseFloat(weight).toFixed(2), replacement: true, draft: true }))
    })

    const {
      packageParser,
      parsedPackages
    } = usePackageParser(
      props,
      drafts,
      selectionState
    )

    // ---- @Product Replacements
    const {
      handleCancelandReplace,
      abortCancelandReplace,
      commitChanges,
      commitCustomPacking,
      commitCancelandReplace,
      handleProductSelect,
      searchProductItem,
      shouldCreateReplacement,
    } = useProductReplacement(props, productState, parsedPackages, startingState, emitPacking, emitEdit, emitReplace)

    // ---- @Product Sorting

    const {
      autoSplit,
      dragState,
      drag,
      createProductDragImg,
      setCustomPacking,
      assignProductToTarget,
      destroyProductDragImg
    } = useProductOrdering(parsedPackages, packingOptions, productState, fetchPacking, emitPacking)

    // ----

    function emitPacked (product: string, component: string, remove?: boolean) {
      const packed = props.packed || []
      const index = packed.findIndex(p => p.id === product && Object.keys(p.components).some(c => c === component))

      if (remove !== true) {
        if (index >= 0) packed.splice(index, 1, { id: product, components: { ...packed[index].components, [component]: true } })
        else packed.push({ id: product, components: { [component]: true } })
      } else {
        if (index >= 0) {
          delete packed[index].components[component]
          // packed[index].components.splice(packed[index].components.indexOf(component), 1)
        }
      }
      emit('update:packed', packed)
    }

    function emitEdit (value) {
      emit('edit', value)
    }

    function emitReplace (value) {
      emit('replaced', value)
      // if (value?.packing) emit('update:packing', value.packing)
      // if (value?.products) emit('update:items', value.packing)
    }

    async function fetchPacking () {
      const { items } = await $directr.get('packing', [{ field: 'id', method: 'exists', match: true }], { limit: 1000 })
      packingEntries.value = items
    }

    function isSelected ({ id, parent }: PackingItem['components'][number]): boolean {
      return selectionState.value.findIndex(f => f.id === parent?.id) > -1
    }

    // NOTE selection, and in effect cancellation / replacement / fulfillment still happens on product-level
    // meaning that components in all packages are "selected" when a product-component is selected, based on the component parent
    function toggleSelection (component: PackingItem['components'][number]): void {
      const { parent } = component

      // if parent is replacement or draft, remove it
      if (parent.replacement && parent.draft) {
        const index = productState.selection.findIndex(s => s.sys?.id === parent.id)

        if (index >= 0) productState.selection.splice(index, 1)
        return
      }

      // add the parent id to the selection
      const index = selectionState.value.findIndex(s => s.id === parent.id)
      if (index >= 0) selectionState.value.splice(index, 1)
      else selectionState.value.push({ id: parent.id, quantity: component.quantity })

      emit('select', selectionState.value)
    }

    function emitPacking (packing) {
      emit('update:packing', packing)
    }

    // ----

    function updateProductQuantity (product, quantity) {
      const draftIndex = productState.selection.findIndex(p => p.sys?.id === product.id)
      if (draftIndex < 0) {
        console.warn('tried to update product quantity, but product was not found in productState.selection')
      }
      productState.selection[draftIndex].quantity = quantity
    }

    async function productQueryHandler (query = [], config = {}) {
      const products = await $directr.get('product', query, { ...config, limit: 50 }).then(({ items }) => $setTo(productState.items, items, 'sys.id'))
      const stockItems = await $directr.get('stock', query, { ...config, limit: 50 }).then(({ items }) => $setTo(productState.items, items, 'sys.id'))
      return products.concat(stockItems)
    }

    watch(() => productState.show, show => {
      if (show === true) {
        if (!$directr) return
        void productQueryHandler()
      }
    })

    async function updatePackingItem (current, index, selection) {
      if (!selection || !selection.value) {
        console.warn('Could not update packing item as selection value was undefined')
        return
      }
      // call setCustomPacking to make sure custom packing is set
      await setCustomPacking()
      productState.showPackingOptions = undefined

      // console.log('updating packing', index, 'current', current, 'selection', selection)
      const packing = [...parsedPackages.value]
      packing.splice(index, 1, {
        weight: current.weight || 1,
        dimensions: { x: current.dimensions?.x || 1, y: current.dimensions?.y || 1, z: current.dimensions?.z || 1 },
        ...selection.value,

        products: current.products.map(p => ({ id: p.id, quantity: p.quantity })),
        components: current.components.map(p => ({ id: p.id, parent: p.parent, quantity: p.quantity }))
      })
      emitPacking(packing.filter(Boolean))
      productState.diff = true
    }

    // set starting state
    onMounted(() => {
      startingState.value = _.cloneDeep([...packageParser()])
    })

    return {
      isMobile: computed(() => $q.screen.lt.sm),
      fulfillableProductTotal,
      parsedPackages,
      shouldCreateReplacement,
      statusMap,
      formatStatus,
      capitalize,
      ProductView,
      productQueryHandler,
      productState,
      dragState,
      drag,
      searchModel: {
        title: { field: 'title', type: 'string' },
        ean: { field: 'ean', type: 'array' },
        sku: { field: 'sku', type: 'string' },
        description: { field: 'description', type: 'string' }
      },
      handleProductSelect,
      handleCancelandReplace,
      commitChanges,
      commitCancelandReplace,
      abortCancelandReplace,
      searchProductItem,
      setCustomPacking,
      autoSplit,
      updateProductQuantity,
      selectionState,
      isSelected,
      toggleSelection,
      subtitle: computed(() => `${props.items.length} products`),
      ITEM_TIP_SCAN: 'Tap the checkmark to mark as picked, or scan the item barcode',
      Status,
      cancelableStatus,
      replaceableStatus,
      replacementTypes: Object.keys(ReplacementType).map(k => ({ value: ReplacementType[k], label: k })),
      replacementReasons,
      togglePackingOptions: async (id) => {
        if (!props.collections?.packing?.length && !packingEntries.value?.length) await fetchPacking()
        // TODO: create structure for collections to simply emit an update, or just use the app contentModule store properly...
        productState.showPackingOptions === id
          ? productState.showPackingOptions = undefined
          : productState.showPackingOptions = id
      },
      packingOptions,
      updatePackingItem,
      emitPacked
    }
  }
})
